import React, { useState } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import { Calculate } from '@mui/icons-material';
import HoursConverterCalculatorDialog from '../../AircraftLifecycleComponents/HoursConverterCalculatorDialog';
import dayjs from 'dayjs';
import { utcToDisplayDateAndTime, displayDateTimeToUTC, getTimezoneLabel } from '../../../utils/timezoneUtils';
import { parseHMMToHours } from '../../../utils/technicalLogBookCarriedForward';
import { tlbFormatFieldProps, formatTechlogOilQuantityLabel } from '../../../utils/technicalLogBookFieldFormat';
import {
  DEFAULT_HOBBS_METER_RECORDS_SECTION,
  isHobbsTotalsFieldPath
} from '../../../constants/technicalLogBookDefinition';

const getValueByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  const keys = path.split('.');
  let v = obj;
  for (const k of keys) v = v?.[k];
  return v;
};

/** Value for a Totals-tab custom field (key may contain dots, or mirror totalBroughtForward./totalCarriedForward.*). */
const getTotalsCustomFieldValue = (formData, key) => {
  if (!key) return undefined;
  if (key.startsWith('totalBroughtForward.') || key.startsWith('totalCarriedForward.')) {
    const primary = getValueByPath(formData, key);
    if (primary !== undefined && primary !== null && primary !== '') return primary;
    // Legacy: updates used totalsCustomFields + dotted path and nested under the first segment
    const mix = formData?.totalsCustomFields;
    if (mix && Object.prototype.hasOwnProperty.call(mix, key)) return mix[key];
    return getValueByPath(formData, `totalsCustomFields.${key}`);
  }
  const mix = formData?.totalsCustomFields;
  if (mix && Object.prototype.hasOwnProperty.call(mix, key)) return mix[key];
  return getValueByPath(formData, `totalsCustomFields.${key}`);
};

const isTotalsCustomKeyMappedToBroughtOrCarried = (key) =>
  typeof key === 'string' &&
  (key.startsWith('totalBroughtForward.') || key.startsWith('totalCarriedForward.'));
  
  const parseBlockTimeToHours = (raw) => {
    if (raw === undefined || raw === null || raw === '') return 0;
    const text = String(raw).trim();
    if (!text) return 0;
    // Support HHMM like "0125" used by sector.blockTime.
    if (/^\d{3,4}$/.test(text)) {
    const padded = text.padStart(4, '0');
    const hours = Number(padded.slice(0, 2));
    const minutes = Number(padded.slice(2, 4));
    if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes >= 60) return 0;
    return hours + minutes / 60;
    }
    // Also support H:MM/HH:MM values in case legacy data exists.
    if (text.includes(':')) {
    const parsed = parseHMMToHours(text);
    return Number.isFinite(parsed) ? parsed : 0;
    }
    // Fallback to decimal hours.
    const decimal = Number(text.replace(',', '.'));
    return Number.isFinite(decimal) ? decimal : 0;
    };
    const TotalsTab = ({ formData, isReadOnly, updateNestedField, updateField, totalsSection, onCalculateTotalBroughtForward, onCalculateTotalCarriedForward, displayTimezone }) => {
  const [hoursCalculatorOpen, setHoursCalculatorOpen] = useState(false);
  const useDefinition = totalsSection && totalsSection.visible !== false && (totalsSection.fields?.length > 0 || totalsSection.customFields?.length > 0);
  const totalsCustomFields = (totalsSection?.customFields || []).filter((f) => f.visible !== false);
  const tzLabel = displayTimezone ? getTimezoneLabel(displayTimezone) : '';
  const dateDisplayValue = (v) => {
    if (!v) return '';
    if (displayTimezone) {
      const d = utcToDisplayDateAndTime(v, displayTimezone).date;
      return d || dayjs(v).format('YYYY-MM-DD');
    }
    return dayjs(v).format('YYYY-MM-DD');
  };
  const dateSaveValue = (dateStr) => {
    if (!dateStr) return null;
    if (displayTimezone) return displayDateTimeToUTC(dateStr, '00:00', displayTimezone);
    return new Date(dateStr);
  };

  const getHoursHelperText = (raw) => {
    if (!raw) return 'H:MM — Stored As: 0.00';
    const value = String(raw).trim();
    if (!value) return 'H:MM — Stored As: 0.00';
    const [hPart = '', mPart = ''] = value.split(':');
    const h = parseInt(hPart, 10);
    const m = parseInt(mPart, 10) || 0;
    const decimal = parseHMMToHours(value);
    if (Number.isNaN(decimal)) return 'H:MM — Stored As: 0.00';
    const safeH = Number.isNaN(h) ? 0 : h;
    const safeM = Number.isNaN(m) ? 0 : m;
    return `h: ${safeH}, m: ${safeM} — Stored As: ${decimal.toFixed(2)}`;
  };

  const normalizeHoursValue = (raw) => {
    if (!raw) return '';
    const value = String(raw).trim();
    if (!value) return '';
    const toHHMM = (decimalHours) => {
      if (!Number.isFinite(decimalHours)) return '';
      const sign = decimalHours < 0 ? '-' : '';
      const abs = Math.abs(decimalHours);
      const totalMinutes = Math.round(abs * 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${sign}${hours}:${String(minutes).padStart(2, '0')}`;
    };
    // Decimal input: "180.46" -> "180:28", "180.2333" -> "180:14"
    if (!value.includes(':')) {
      const decimal = Number(value.replace(',', '.'));
      if (!Number.isFinite(decimal)) return '';
      return toHHMM(decimal);
    }
    // HH:MM or HH:MM:SS input
    const decimal = parseHMMToHours(value);
    if (!Number.isFinite(decimal)) return '';
    return toHHMM(decimal);
  };

  const toHoursDisplayValue = (raw) => {
    if (raw === undefined || raw === null || raw === '') return '';
    if (typeof raw === 'number') return normalizeHoursValue(raw);
    return String(raw);
  };

  const updateByPath = (path, value) => {
    if (path.startsWith('totalBroughtForward.')) {
      const key = path.slice('totalBroughtForward.'.length);
      updateNestedField('totalBroughtForward', key, value);
    } else if (path.startsWith('totalCarriedForward.')) {
      if (!formData.totalCarriedForward) updateField('totalCarriedForward', {});
      const key = path.slice('totalCarriedForward.'.length);
      updateNestedField('totalCarriedForward', key, value);
    } else {
      updateField(path, value);
    }
  };
  const activeSectors = [1, 2, 3, 4, 5]
.map((idx) => formData?.[`sector${idx}`])
.filter((sector) => sector && typeof sector === 'object');
const totalBlockTimeHours = activeSectors.reduce((sum, sector) => {
return sum + parseBlockTimeToHours(sector?.blockTime);
}, 0);
const totalFuelConsumption = activeSectors.reduce((sum, sector) => {
const value = Number(sector?.fuelConsumption ?? 0);
return sum + (Number.isFinite(value) ? value : 0);
}, 0);
const totalOilConsumption = activeSectors.reduce((sum, sector) => {
const value = Number(sector?.oilConsumption ?? 0);
return sum + (Number.isFinite(value) ? value : 0);
}, 0);
const fuelConsumptionPerHr = totalBlockTimeHours > 0 ? totalFuelConsumption / totalBlockTimeHours : 0;
const oilConsumptionPerHr = totalBlockTimeHours > 0 ? totalOilConsumption / totalBlockTimeHours : 0;

  const renderTotalsFields = (fields) => fields.map((field) => {
    const value = getValueByPath(formData, field.path);
    const disabled = isReadOnly || field.readOnly;
    const label = formatTechlogOilQuantityLabel(field.label, field.path);

    if (field.readOnly) {
      return (
        <Grid item xs={12} md={4} key={field.path}>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="body2">
            {value != null && value !== '' ? String(value) : '—'}
          </Typography>
        </Grid>
      );
    }

    switch (field.type) {
      case 'number':
        return (
          <Grid item xs={12} md={4} key={field.path}>
            <TextField
              fullWidth
              label={label}
              type="number"
              value={value ?? ''}
              onChange={(e) =>
                updateByPath(
                  field.path,
                  e.target.value === '' ? undefined : Number(e.target.value)
                )
              }
              disabled={disabled}
            />
          </Grid>
        );
      case 'hours':
        return (
          <Grid item xs={12} md={4} key={field.path}>
            <TextField
              fullWidth
              label={label}
              value={toHoursDisplayValue(value)}
              onChange={(e) => updateByPath(field.path, e.target.value)}
              onBlur={(e) =>
                updateByPath(field.path, normalizeHoursValue(e.target.value))
              }
              disabled={disabled}
              {...tlbFormatFieldProps(value, { fieldType: 'hours' }, getHoursHelperText(value))}
            />
          </Grid>
        );
      default:
        return (
          <Grid item xs={12} md={4} key={field.path}>
            <TextField
              fullWidth
              label={label}
              value={value ?? ''}
              onChange={(e) => updateByPath(field.path, e.target.value)}
              disabled={disabled}
            />
          </Grid>
        );
    }
  });

  if (useDefinition) {
    const broughtForward = (totalsSection.fields || []).filter(
      (f) => f.path?.startsWith('totalBroughtForward.') && f.visible !== false
    );
    const carriedForward = (totalsSection.fields || []).filter(
      (f) => f.path?.startsWith('totalCarriedForward.') && f.visible !== false
    );
    const broughtForwardStandard = broughtForward.filter((field) => !isHobbsTotalsFieldPath(field.path));
    const broughtForwardHobbs = broughtForward.filter((field) => isHobbsTotalsFieldPath(field.path));
    const carriedForwardStandard = carriedForward.filter((field) => !isHobbsTotalsFieldPath(field.path));
    const carriedForwardHobbs = carriedForward.filter((field) => isHobbsTotalsFieldPath(field.path));
    const hobbsSectionLabel = DEFAULT_HOBBS_METER_RECORDS_SECTION.label;
    return (
      <Grid container spacing={2}>
        {(onCalculateTotalBroughtForward || onCalculateTotalCarriedForward) && (
          <Grid item xs={12}>
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            {onCalculateTotalBroughtForward && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Calculate />}
                  onClick={onCalculateTotalBroughtForward}
                >
                  Calculate Total Brought Forward
                </Button>
              )}
              {onCalculateTotalCarriedForward && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Calculate />}
                onClick={onCalculateTotalCarriedForward}
              >
                Calculate Total Carried Forward
              </Button>
              )}
              <Tooltip title="Hours converter (decimal ⇄ HH:MM)">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setHoursCalculatorOpen(true)}
                  aria-label="Open hours converter calculator"
                >
                  <Calculate fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              Carried Forward = Brought Forward + sum of all sectors on this sheet (including custom fields).
            </Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Total Brought Forward
          </Typography>
          <Grid container spacing={2}>
            {renderTotalsFields(broughtForwardStandard)}
          </Grid>
        </Grid>
        {broughtForwardHobbs.length > 0 && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              {hobbsSectionLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Brought forward readings; carried forward uses the last sector entry on this sheet when present.
            </Typography>
            <Grid container spacing={2}>
              {renderTotalsFields(broughtForwardHobbs)}
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Total Carried Forward
          </Typography>
          <Grid container spacing={2}>
            {renderTotalsFields(carriedForwardStandard)}
          </Grid>
        </Grid>
        {carriedForwardHobbs.length > 0 && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              {hobbsSectionLabel}
            </Typography>
            <Grid container spacing={2}>
              {renderTotalsFields(carriedForwardHobbs)}
            </Grid>
          </Grid>
        )}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Fuel And Oil Usage
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Fuel Consumption per Hr"
                type="number"
                value={fuelConsumptionPerHr.toFixed(2)}
                disabled
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Oil Consumption per Hr (ml)"
                type="number"
                value={oilConsumptionPerHr.toFixed(2)}
                disabled
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </Grid>
        {totalsCustomFields.length > 0 && (
          <>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                Totals custom fields
              </Typography>
              <Grid container spacing={2}>
                {totalsCustomFields.map((field) => {
                  const path = `totalsCustomFields.${field.key}`;
                  const mappedToTotals = isTotalsCustomKeyMappedToBroughtOrCarried(field.key);
                  const value = getTotalsCustomFieldValue(formData, field.key);
                  const disabled = isReadOnly || field.readOnly;
                  const label = formatTechlogOilQuantityLabel(field.label || field.key, field.key);
                  const setVal = (v) =>
                    mappedToTotals ? updateByPath(field.key, v) : updateField(path, v);
                  if (field.readOnly) {
                    return (
                      <Grid item xs={12} md={4} key={field.key}>
                        <Typography variant="caption" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography variant="body2">
                          {value != null && value !== '' ? String(value) : '—'}
                        </Typography>
                      </Grid>
                    );
                  }
                  switch (field.type) {
                    case 'number':
                      return (
                        <Grid item xs={12} md={4} key={field.key}>
                          <TextField
                            fullWidth
                            type="number"
                            label={label}
                            value={value ?? ''}
                            onChange={(e) =>
                              setVal(e.target.value === '' ? undefined : Number(e.target.value))
                            }
                            disabled={disabled}
                            required={!!field.required}
                          />
                        </Grid>
                      );
                    case 'date':
                      return (
                        <Grid item xs={12} md={4} key={field.key}>
                          <TextField
                            fullWidth
                            type="date"
                            label={tzLabel ? `${label} (${tzLabel})` : label}
                            value={dateDisplayValue(value)}
                            onChange={(e) =>
                              setVal(e.target.value ? dateSaveValue(e.target.value) : null)
                            }
                            disabled={disabled}
                            required={!!field.required}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      );
                    case 'time':
                      return (
                        <Grid item xs={12} md={4} key={field.key}>
                          <TextField
                            fullWidth
                            type="time"
                            label={label}
                            value={value ?? ''}
                            onChange={(e) => setVal(e.target.value)}
                            disabled={disabled}
                            required={!!field.required}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      );
                    case 'checkbox':
                      return (
                        <Grid item xs={12} md={4} key={field.key}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!value}
                                onChange={(e) => setVal(e.target.checked)}
                                disabled={disabled}
                              />
                            }
                            label={label}
                          />
                        </Grid>
                      );
                    case 'dropdown':
                      return (
                        <Grid item xs={12} md={4} key={field.key}>
                          <FormControl fullWidth disabled={disabled}>
                            <InputLabel>{label}</InputLabel>
                            <Select
                              value={value ?? ''}
                              label={label}
                              onChange={(e) => setVal(e.target.value)}
                            >
                              {(field.options || []).map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label ?? opt.value}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      );
                    case 'multiline':
                      return (
                        <Grid item xs={12} key={field.key}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label={label}
                            value={value ?? ''}
                            onChange={(e) => setVal(e.target.value)}
                            disabled={disabled}
                            required={!!field.required}
                          />
                        </Grid>
                      );
                    case 'hours':
                      return (
                        <Grid item xs={12} md={4} key={field.key}>
                          <TextField
                            fullWidth
                            label={label}
                            value={toHoursDisplayValue(value)}
                            onChange={(e) => setVal(e.target.value)}
                            onBlur={(e) =>
                              setVal(normalizeHoursValue(e.target.value))
                            }
                            disabled={disabled}
                            required={!!field.required}
                            {...tlbFormatFieldProps(value, { fieldType: 'hours' }, getHoursHelperText(value))}
                          />
                        </Grid>
                      );
                    default:
                      return (
                        <Grid item xs={12} md={4} key={field.key}>
                          <TextField
                            fullWidth
                            label={label}
                            value={value ?? ''}
                            onChange={(e) => setVal(e.target.value)}
                            disabled={disabled}
                            required={!!field.required}
                          />
                        </Grid>
                      );
                  }
                })}
              </Grid>
            </Grid>
          </>
        )}
        <HoursConverterCalculatorDialog
          open={hoursCalculatorOpen}
          onClose={() => setHoursCalculatorOpen(false)}
        />
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      {(onCalculateTotalBroughtForward || onCalculateTotalCarriedForward) && (
        <Grid item xs={12}>
          <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          {onCalculateTotalBroughtForward && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Calculate />}
                onClick={onCalculateTotalBroughtForward}
              >
                Calculate Total Brought Forward
              </Button>
            )}
            {onCalculateTotalCarriedForward && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Calculate />}
              onClick={onCalculateTotalCarriedForward}
            >
              Calculate Total Carried Forward
            </Button>
            )}
            <Tooltip title="Hours converter (decimal ⇄ HH:MM)">
              <IconButton
                size="small"
                color="primary"
                onClick={() => setHoursCalculatorOpen(true)}
                aria-label="Open hours converter calculator"
              >
                <Calculate fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mb: 1.5 }}
          >
            Carried Forward = Brought Forward + sum of all sectors on this sheet (including custom fields).
          </Typography>
        </Grid>
      )}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Total Brought Forward</Typography>
      </Grid>
      {formData.totalBroughtForward && Object.entries(formData.totalBroughtForward).map(([key, value]) => (
        <Grid item xs={12} md={4} key={key}>
          <TextField
            fullWidth
            label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
            type="number"
            value={value || 0}
            onChange={(e) => updateNestedField('totalBroughtForward', key, parseFloat(e.target.value) || 0)}
            disabled={isReadOnly}
          />
        </Grid>
      ))}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Total Carried Forward</Typography>
      </Grid>
      {(formData.totalCarriedForward && Object.keys(formData.totalCarriedForward).length > 0
        ? Object.entries(formData.totalCarriedForward)
        : formData.totalBroughtForward
          ? Object.keys(formData.totalBroughtForward).map(key => [key, formData.totalCarriedForward?.[key] || 0])
          : [
              ['airframeHours', 0],
              ['landings', 0],
              ['engine1Hours', 0],
              ['engine2Hours', 0],
              ['lhEngineCyclesCT', 0],
              ['lhEngineCyclesPT', 0],
              ['lhEngineCyclesIMP', 0],
              ['rhEngineCyclesCT', 0],
              ['rhEngineCyclesPT', 0],
              ['rhEngineCyclesIMP', 0]
            ]
      ).map(([key, value]) => (
        <Grid item xs={12} md={4} key={key}>
          <TextField
            fullWidth
            label={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
            type="number"
            value={value || 0}
            onChange={(e) => {
              if (!formData.totalCarriedForward) updateField('totalCarriedForward', {});
              updateNestedField('totalCarriedForward', key, parseFloat(e.target.value) || 0);
            }}
            disabled={isReadOnly}
          />
        </Grid>
      ))}
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>Fuel And Oil Usage</Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Fuel Consumption per Hr"
          type="number"
          value={fuelConsumptionPerHr.toFixed(2)}
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          label="Oil Consumption per Hr (ml)"
          type="number"
          value={oilConsumptionPerHr.toFixed(2)}
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
      {totalsCustomFields.length > 0 && (
        <>
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>Totals custom fields</Typography>
          </Grid>
          {totalsCustomFields.map((field) => {
            const path = `totalsCustomFields.${field.key}`;
            const mappedToTotals = isTotalsCustomKeyMappedToBroughtOrCarried(field.key);
            const value = getTotalsCustomFieldValue(formData, field.key);
            const disabled = isReadOnly || field.readOnly;
            const label = formatTechlogOilQuantityLabel(field.label || field.key, field.key);
            const setVal = (v) =>
              mappedToTotals ? updateByPath(field.key, v) : updateField(path, v);
            if (field.readOnly) {
              return (
                <Grid item xs={12} md={4} key={field.key}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2">{value != null && value !== '' ? String(value) : '—'}</Typography>
                </Grid>
              );
            }
            switch (field.type) {
              case 'number':
                return (
                  <Grid item xs={12} md={4} key={field.key}>
                    <TextField fullWidth type="number" label={label} value={value ?? ''} onChange={(e) => setVal(e.target.value === '' ? undefined : Number(e.target.value))} disabled={disabled} required={!!field.required} />
                  </Grid>
                );
              case 'date':
                return (
                  <Grid item xs={12} md={4} key={field.key}>
                    <TextField fullWidth type="date" label={tzLabel ? `${label} (${tzLabel})` : label} value={dateDisplayValue(value)} onChange={(e) => setVal(e.target.value ? dateSaveValue(e.target.value) : null)} disabled={disabled} required={!!field.required} InputLabelProps={{ shrink: true }} />
                  </Grid>
                );
              case 'time':
                return (
                  <Grid item xs={12} md={4} key={field.key}>
                    <TextField fullWidth type="time" label={label} value={value ?? ''} onChange={(e) => setVal(e.target.value)} disabled={disabled} required={!!field.required} InputLabelProps={{ shrink: true }} />
                  </Grid>
                );
              case 'checkbox':
                return (
                  <Grid item xs={12} md={4} key={field.key}>
                    <FormControlLabel control={<Checkbox checked={!!value} onChange={(e) => setVal(e.target.checked)} disabled={disabled} />} label={label} />
                  </Grid>
                );
              case 'dropdown':
                return (
                  <Grid item xs={12} md={4} key={field.key}>
                    <FormControl fullWidth disabled={disabled}>
                      <InputLabel>{label}</InputLabel>
                      <Select value={value ?? ''} label={label} onChange={(e) => setVal(e.target.value)}>
                        {(field.options || []).map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label ?? opt.value}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                );
              case 'multiline':
                return (
                  <Grid item xs={12} key={field.key}>
                    <TextField fullWidth multiline minRows={2} label={label} value={value ?? ''} onChange={(e) => setVal(e.target.value)} disabled={disabled} required={!!field.required} />
                  </Grid>
                );
              default:
                return (
                  <Grid item xs={12} md={4} key={field.key}>
                    <TextField fullWidth label={label} value={value ?? ''} onChange={(e) => setVal(e.target.value)} disabled={disabled} required={!!field.required} />
                  </Grid>
                );
            }
          })}
        </>
      )}
      <HoursConverterCalculatorDialog
        open={hoursCalculatorOpen}
        onClose={() => setHoursCalculatorOpen(false)}
      />
    </Grid>
  );
};

export default TotalsTab;

